import { Task, TaskListProps } from "@/models";
import {
  Accordion,
  AccordionHeader,
  AccordionItem,
  AccordionPanel,
  Button,
  Caption1,
  Menu,
  MenuTrigger,
  Skeleton,
  SkeletonItem,
} from "@fluentui/react-components";
import { MoreHorizontal20Regular } from "@fluentui/react-icons";
import React from "react";
import "../../styles/TaskList.css";

const dateFormatter = new Intl.DateTimeFormat(undefined, { dateStyle: "medium" });

const groupHistoryByDate = (tasks: Task[]): { label: string; tasks: Task[] }[] => {
  const today = dateFormatter.format(new Date());
  const yesterday = dateFormatter.format(new Date(Date.now() - 86_400_000));

  const groups = new Map<string, Task[]>();

  for (const task of tasks) {
    const raw = task.date ?? "";
    const label = raw === today ? "Today" : raw === yesterday ? "Yesterday" : raw || "Older";
    if (!groups.has(label)) groups.set(label, []);
    groups.get(label)!.push(task);
  }

  return Array.from(groups.entries()).map(([label, tasks]) => ({ label, tasks }));
};

const TaskList: React.FC<TaskListProps> = ({
  historyTasks = [],
  inProgressTasks,
  completedTasks,
  onTaskSelect,
  loading,
  selectedTaskId,
  showInProgress = true,
  showCompleted = true,
}) => {
  const renderTaskItem = (task: Task) => {
    const isActive = task.id === selectedTaskId;

    return (
      <div
        key={task.id}
        className={`task-tab${isActive ? " active" : ""}`}
        role="button"
        tabIndex={0}
        onClick={() => onTaskSelect(task.id)}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            onTaskSelect(task.id);
          }
        }}
      >
        <div className="sideNavTick" />
        <div className="left">
          <div className="task-name-truncated" title={task.name}>
            {task.name}
          </div>
          {task.date && task.status == "completed" && (
            <Caption1 className="task-list-task-date">{task.date}</Caption1>
          )}
          {task.status == "inprogress" && (
            <Caption1 className="task-list-task-date">{`${task?.completed_steps} of ${task?.total_steps} completed`}</Caption1>
          )}
        </div>
        <Menu>
          <MenuTrigger>
            <Button
              appearance="subtle"
              icon={<MoreHorizontal20Regular />}
              onClick={(e: React.MouseEvent) => e.stopPropagation()}
              className="task-menu-button"
            />
          </MenuTrigger>
        </Menu>
      </div>
    );
  };

  const renderSkeleton = (key: string) => (
    <div key={key} className="task-skeleton-container">
      <Skeleton aria-label="Loading task">
        <div className="task-skeleton-wrapper">
          <SkeletonItem shape="rectangle" animation="wave" size={24} />
        </div>
      </Skeleton>
    </div>
  );

  return (
    <div className="task-list-container">
      <Accordion defaultOpenItems={["1"]} collapsible multiple>
        <AccordionItem value="1">
          <AccordionHeader expandIconPosition="end">
            History
          </AccordionHeader>
          <AccordionPanel>
            {loading
              ? Array.from({ length: 5 }, (_, i) =>
                renderSkeleton(`history-${i}`)
              )
              : historyTasks.length > 0
                ? groupHistoryByDate(historyTasks).map(({ label, tasks }) => (
                  <React.Fragment key={label}>
                    <div className="history-date-group-label">{label}</div>
                    {tasks.map(renderTaskItem)}
                  </React.Fragment>
                ))
                : <Caption1 style={{ padding: '0.5rem', color: '#666' }}>No conversation history</Caption1>}
          </AccordionPanel>
        </AccordionItem>
        {showInProgress && (
          <AccordionItem value="2">
            <AccordionHeader expandIconPosition="end">
              In progress
            </AccordionHeader>
            <AccordionPanel>
              {loading
                ? Array.from({ length: 5 }, (_, i) =>
                  renderSkeleton(`in-progress-${i}`)
                )
                : inProgressTasks.map(renderTaskItem)}
            </AccordionPanel>
          </AccordionItem>
        )}
        {showCompleted && (
          <AccordionItem value="3">
            <AccordionHeader expandIconPosition="end">Completed</AccordionHeader>
            <AccordionPanel>
              {loading
                ? Array.from({ length: 5 }, (_, i) =>
                  renderSkeleton(`completed-${i}`)
                )
                : completedTasks.map(renderTaskItem)}
            </AccordionPanel>
          </AccordionItem>
        )}
      </Accordion>
    </div>
  );
};

export default TaskList;
